import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, ArrowRight, CheckCircle2, CheckCircle } from 'lucide-react';
import FoundingForm from './FoundingForm';

const stepGroup = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.35 } },
};
const stepItem = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};
const lineDraw = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: { pathLength: 1, opacity: 1, transition: { duration: 0.5 } },
};

const SmartScopingSection = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  return (
    <section id="smart-scoping" className="py-24 relative bg-gray-950">
      <div className="container mx-auto px-6">

        {/* Header */}
        <motion.div
          className="max-w-3xl mx-auto text-center mb-12"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 border border-orange-900/50 bg-orange-950/25 px-4 py-2 mb-8 text-orange-400 text-xs font-black uppercase tracking-widest" style={{ borderRadius: '2px' }}>
            <Zap className="w-3.5 h-3.5" />
            AI-backed project scoping
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight tracking-tight mb-6">
            <span className="text-white">Answer what's relevant.</span>
            <span className="block text-orange-500">Get a complete project map.</span>
          </h2>

          <p className="text-base text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Our in-house AI asks follow-ups based on what you tell it — so the questionnaire adapts
            to your project instead of making you fill in a generic form.
            No surprises on the invoice later.
          </p>
        </motion.div>

        {/* Animated diagram */}
        <motion.div
          className="max-w-2xl mx-auto border border-gray-800 bg-gray-900/60 p-6 md:p-8 mb-10"
          style={{ borderRadius: '2px' }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.4 }}
          variants={stepGroup}
        >
          {/* Q1 */}
          <motion.div variants={stepItem} className="flex items-center justify-center gap-2 mb-4 flex-wrap">
            <span className="text-xs font-black text-orange-500 font-mono">Q1</span>
            <div className="border border-orange-500/30 bg-orange-950/25 text-orange-100 text-sm px-4 py-2" style={{ borderRadius: '2px' }}>
              What's your industry?
            </div>
          </motion.div>

          {/* Branch lines */}
          <svg width="100%" height="28" className="block mb-1" aria-hidden="true">
            <motion.line variants={lineDraw} x1="50%" y1="0" x2="17%" y2="28" stroke="#f97316" strokeWidth="1.5" />
            <motion.line variants={lineDraw} x1="50%" y1="0" x2="50%" y2="28" stroke="#f97316" strokeWidth="1.5" />
            <motion.line variants={lineDraw} x1="50%" y1="0" x2="83%" y2="28" stroke="#f97316" strokeWidth="1.5" />
          </svg>

          {/* Industry pills — flex row on mobile */}
          <motion.div variants={stepGroup} className="flex items-center justify-between gap-2 mb-5 flex-wrap sm:flex-nowrap">
            {['E-commerce', 'Healthcare', 'Real estate'].map(label => (
              <motion.div
                key={label}
                variants={stepItem}
                className="flex-1 min-w-0 text-center border border-gray-700 bg-gray-800/50 text-gray-300 text-xs px-3 py-2 whitespace-nowrap"
                style={{ borderRadius: '2px' }}
              >
                {label}
              </motion.div>
            ))}
          </motion.div>

          {/* Q2 */}
          <motion.div variants={stepItem} className="flex items-center justify-center gap-2 mb-4 flex-wrap">
            <span className="text-xs font-black text-orange-500 font-mono">Q2</span>
            <div className="border border-orange-500/30 bg-orange-950/25 text-orange-100 text-sm px-4 py-2" style={{ borderRadius: '2px' }}>
              M-Pesa checkout needed?
            </div>
            <span className="text-xs text-gray-600 hidden sm:inline">— only because you picked E-commerce</span>
          </motion.div>

          {/* Result */}
          <motion.div variants={stepGroup} className="mt-4 pt-4 border-t border-gray-800">
            <motion.div variants={stepItem} className="flex items-center justify-center gap-2 flex-wrap mb-3">
              {['Frontend', 'Integrations', 'Database'].map((tag, i, arr) => (
                <React.Fragment key={tag}>
                  <div className="border border-gray-700 bg-gray-800/50 text-gray-300 text-xs px-3 py-1.5" style={{ borderRadius: '2px' }}>{tag}</div>
                  {i < arr.length - 1 && <ArrowRight className="w-3 h-3 text-gray-600 shrink-0" />}
                </React.Fragment>
              ))}
            </motion.div>
            <motion.div variants={stepItem} className="flex items-center justify-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-orange-500 shrink-0" />
              <span className="text-sm text-gray-300">Your project scope is ready to review</span>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <motion.button
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 font-black text-sm uppercase tracking-wide inline-flex items-center gap-2 transition-colors duration-200"
            style={{ borderRadius: '2px' }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setIsFormOpen(true)}
          >
            Map my project — free
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </motion.div>
      </div>

      {/* Founding form modal */}
      <AnimatePresence>
        {isFormOpen && (
          <FoundingForm
            onClose={() => setIsFormOpen(false)}
            onSuccess={(result) => {
              setSubmissionResult(result);
              setIsFormOpen(false);
            }}
          />
        )}
      </AnimatePresence>

      {/* Success modal */}
      <AnimatePresence>
        {submissionResult && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSubmissionResult(null)}
          >
            <motion.div
              className="bg-gray-900 border border-gray-700 p-8 max-w-md w-full"
              style={{ borderRadius: '2px' }}
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-14 h-14 bg-orange-500 flex items-center justify-center mx-auto mb-4" style={{ borderRadius: '2px' }}>
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-black text-white mb-3 uppercase tracking-tight">Application Received</h3>
                <p className="text-gray-400 mb-6 text-sm leading-relaxed">
                  Thanks for mapping out your project. We'll review it and get back to you within 24 hours.
                </p>
                <div className="border border-orange-900/50 bg-orange-950/20 p-4 mb-6" style={{ borderRadius: '2px' }}>
                  <p className="text-orange-400 text-xs font-black font-mono uppercase tracking-widest">Request ID: {submissionResult.projectId}</p>
                  <p className="text-gray-600 text-xs mt-1">Keep this ID for your records</p>
                </div>
                <motion.button
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 font-black text-sm uppercase tracking-wide transition-colors duration-200"
                  style={{ borderRadius: '2px' }}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSubmissionResult(null)}
                >
                  Got it, thanks!
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default SmartScopingSection;
